/**
 * NavTabs Component - Navigation Tabs
 * 
 * OPARA v11.7 - Mobile Hamburger Menu
 * - Desktop: Full navigation bar with tabs
 * - Mobile/Tablet: Hamburger menu with all navigation items
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, Settings, LogOut, CreditCard, Globe, Menu, X, Search, BarChart3, History, FileText, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from '../hooks/useTranslation';

// Language options with flags
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', path: '/en/' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', path: '/tr/' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', path: '/de/' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', path: '/fr/' },
  { code: 'es', name: 'Español', flag: '🇪🇸', path: '/es/' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', path: '/ar/' },
];

/**
 * TabButton - Single tab button (Desktop)
 */
const TabButton = ({ label, isActive, onClick, color = 'green', badge = false }) => {
  const colorClasses = {
    green: 'bg-[#00D26A]',
    blue: 'bg-[#3B82F6]',
    yellow: 'bg-[#FFDD59]',
    orange: 'bg-[#F97316]',
    red: 'bg-[#EF4444]'
  };

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center justify-center gap-2 w-[140px] py-2.5 text-sm font-medium transition-all rounded-lg whitespace-nowrap flex-shrink-0 ${
        badge
          ? 'bg-red-900/30 text-white ring-1 ring-red-500/50'
          : isActive
            ? 'bg-gray-800/80 text-white'
            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      }`}
      data-testid={`nav-tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className={`w-2 h-2 rounded-full transition-all ${
        badge ? 'bg-[#EF4444] animate-pulse' : isActive ? colorClasses[color] : 'bg-gray-600'
      }`} />
      <span className="truncate">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 border border-black" />
      )}
    </button>
  );
};

/**
 * MobileMenuItem - Menu item for mobile hamburger menu
 */
const MobileMenuItem = ({ label, isActive, onClick, icon: Icon, color = 'green', badge = false }) => {
  const colorClasses = {
    green: 'bg-[#00D26A]',
    blue: 'bg-[#3B82F6]',
    yellow: 'bg-[#FFDD59]',
    orange: 'bg-[#F97316]',
    red: 'bg-[#EF4444]'
  };

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
        badge
          ? 'bg-red-900/30 text-white'
          : isActive
            ? 'bg-gray-800 text-white'
            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${badge ? 'bg-[#EF4444] animate-pulse' : isActive ? colorClasses[color] : 'bg-gray-600'}`} />
      {Icon && <Icon className="w-4 h-4" />}
      <span>{label}</span>
      {badge && <span className="ml-auto w-2.5 h-2.5 rounded-full bg-red-500" />}
    </button>
  );
};

/**
 * NavTabs - Main navigation component
 */
export default function NavTabs({ user, currentPage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, token } = useAuth();
  const { t, locale, isRTL } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // C4nav: OPARA YAPSIN staff-gate = STRICT @personaajans.com domain (plan_type DEGIL) + rescue-havuz badge poll.
  const isStaff = !!(user?.email && user.email.toLowerCase().endsWith('@personaajans.com'));
  const [rescueCount, setRescueCount] = useState(0);
  useEffect(() => {
    if (!isStaff || !token) return;  // non-staff'ta poll HIC kurulmaz
    let active = true;
    const fetchRescueCount = async () => {
      try {
        const res = await axios.get(`${API}/team/rescue-pool`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setRescueCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch (e) {
        if (active) setRescueCount(0);
      }
    };
    fetchRescueCount();
    const interval = setInterval(fetchRescueCount, 60000);  // HomePage:407 team-poll deseni
    return () => { active = false; clearInterval(interval); };
  }, [isStaff, token]);

  // Get locale-aware paths
  const getLocalePath = (route) => {
    const routes = {
      en: {
        home: '/en/',
        analysis: '/en/',
        bulk: '/en/bulk-analysis',
        history: '/en/history',
        report: '/en/reports',
        pricing: '/en/pricing',
        settings: '/en/settings',
        login: '/en/login',
        rescue: '/en/rescue-pool'
      },
      tr: {
        home: '/tr/',
        analysis: '/tr/',
        bulk: '/tr/toplu-analiz',
        history: '/tr/analizgecmisi',
        report: '/tr/rapor',
        pricing: '/tr/kredi-paketleri',
        settings: '/tr/hesap-ayarlari',
        login: '/tr/giris',
        rescue: '/tr/kurtarma-havuzu'
      },
      ar: {
        home: '/ar/',
        analysis: '/ar/',
        bulk: '/ar/bulk-analysis',
        history: '/ar/history',
        report: '/ar/reports',
        pricing: '/ar/pricing',
        settings: '/ar/settings',
        login: '/ar/login',
        rescue: '/ar/rescue-pool'
      },
      fr: {
        home: '/fr/',
        analysis: '/fr/',
        bulk: '/fr/bulk-analysis',
        history: '/fr/history',
        report: '/fr/reports',
        pricing: '/fr/pricing',
        settings: '/fr/settings',
        login: '/fr/login',
        rescue: '/fr/rescue-pool'
      },
      de: {
        home: '/de/',
        analysis: '/de/',
        bulk: '/de/bulk-analysis',
        history: '/de/history',
        report: '/de/reports',
        pricing: '/de/pricing',
        settings: '/de/settings',
        login: '/de/login',
        rescue: '/de/rescue-pool'
      },
      es: {
        home: '/es/',
        analysis: '/es/',
        bulk: '/es/bulk-analysis',
        history: '/es/history',
        report: '/es/reports',
        pricing: '/es/pricing',
        settings: '/es/settings',
        login: '/es/login',
        rescue: '/es/rescue-pool'
      }
    };
    
    return routes[locale]?.[route] || routes.en[route];
  };

  // Get plan display name based on locale
  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': t('plan.free'),
      'basic': 'Basic',
      'pro': 'Pro',
      'business': 'Business',
      'enterprise': 'Enterprise',
      'persona_staff': t('plan.staff'),
      'admin': 'Admin'
    };
    return planNames[planType] || planType || t('plan.free');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Determine active tab based on currentPage prop or URL
  const getActiveTab = () => {
    if (currentPage) return currentPage;
    const path = location.pathname;
    if (path.includes('rescue-pool') || path.includes('kurtarma-havuzu')) return 'oparayapsin';
    if (path.includes('toplu-analiz') || path.includes('bulk-analysis')) return 'topluanaliz';
    if (path.includes('analizgecmisi') || path.includes('history')) return 'analizgecmisi';
    if (path.includes('rapor') || path.includes('report')) return 'rapor';
    return 'analiz';
  };

  const activeTab = getActiveTab();

  const tabs = [
    { id: 'analiz', label: t('nav.analysis'), path: getLocalePath('analysis'), color: 'green', icon: Search },
    { id: 'topluanaliz', label: t('nav.bulk_analysis'), path: getLocalePath('bulk'), color: 'orange', icon: BarChart3 },
    { id: 'analizgecmisi', label: t('nav.history'), path: getLocalePath('history'), color: 'blue', icon: History },
    { id: 'rapor', label: t('nav.report'), path: getLocalePath('report'), color: 'yellow', icon: FileText },
    // C4nav: OPARA YAPSIN yalniz staff'ta; rescueCount>0 -> kirmizi badge (badge alani TabButton/MobileMenuItem'e gecer).
    ...(isStaff ? [{ id: 'oparayapsin', label: t('nav.opara_yapsin'), path: getLocalePath('rescue'), color: 'red', icon: AlertTriangle, badge: rescueCount > 0 }] : [])
  ];

  const handleMobileNavigation = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="px-3 md:px-6 py-2 md:py-3">
        <div className="flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-4 md:gap-6">
            {/* Logo */}
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
              onClick={() => navigate(getLocalePath('home'))}
              data-testid="nav-logo"
            >
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA Logo" 
                className="h-7 md:h-9 w-auto object-contain"
              />
            </div>

            {/* Divider - Desktop only */}
            <div className="hidden lg:block w-px h-8 bg-gray-700 flex-shrink-0" />

            {/* Desktop Tabs - Hidden on mobile/tablet */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="nav-tabs">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  label={tab.label}
                  isActive={activeTab === tab.id}
                  onClick={() => navigate(tab.path)}
                  color={tab.color}
                  badge={tab.badge}
                />
              ))}
            </nav>
          </div>

          {/* Right: Desktop Credits + Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Desktop Credits - Hidden on mobile/tablet */}
            <div className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gray-800/80 rounded-lg border border-gray-700">
              <span className="text-sm text-gray-400">{t('nav.credits')}</span>
              <span className="text-sm font-bold text-white">
                {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining || 0}
              </span>
            </div>

            {/* Desktop Upgrade Button - Hidden on mobile/tablet */}
            <Link to={getLocalePath('pricing')} className="hidden lg:block">
              <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-sm h-9 px-4">
                {t('nav.upgrade')}
              </Button>
            </Link>

            {/* User Avatar Dropdown - Always visible */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]"
                  data-testid="user-menu-trigger"
                >
                  <User className="w-4 h-4 text-black" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                <DropdownMenuLabel className="text-white">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">
                      {user?.first_name} {user?.last_name}
                      {user?.plan_type === "persona_staff" && (
                        <span className="text-xs text-[#FFDD59] ml-2">{t('nav.staff_badge')}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                    </p>
                    {/* Show credits in dropdown for all screens */}
                    <p className="text-xs text-gray-400">
                      {t('nav.credits')} <span className="text-white font-bold">
                        {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining || 0}
                      </span>
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-700" />
                
                <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                  <Link to={getLocalePath('settings')} className="flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('nav.account_settings')}
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                  <Link to={getLocalePath('pricing')} className="flex items-center">
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t('nav.pricing')}
                  </Link>
                </DropdownMenuItem>
                
                {/* Language Submenu */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer text-gray-300 hover:text-black focus:text-black data-[state=open]:text-black hover:bg-gray-100 focus:bg-gray-100 data-[state=open]:bg-gray-100">
                    <Globe className="w-4 h-4 mr-2" />
                    {t('nav.change_language')}
                    <span className="ml-auto text-lg">{LANGUAGES.find(l => l.code === locale)?.flag || '🌐'}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="bg-gray-900 border-gray-700">
                      {LANGUAGES.map((lang) => (
                        <DropdownMenuItem
                          key={lang.code}
                          className={`cursor-pointer hover:bg-gray-800 ${
                            locale === lang.code 
                              ? 'text-[#FFDD59] bg-gray-800' 
                              : 'text-gray-300 hover:text-white'
                          }`}
                          onClick={() => navigate(lang.path)}
                        >
                          <span className="text-lg mr-2">{lang.flag}</span>
                          {lang.name}
                          {locale === lang.code && <span className="ml-auto">✓</span>}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                
                <DropdownMenuSeparator className="bg-gray-700" />
                
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800"
                  data-testid="logout-menu-item"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Hamburger Menu Button - Mobile/Tablet only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-gray-800/80 border border-gray-700 hover:bg-gray-700 transition-colors"
              data-testid="mobile-menu-trigger"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Slide down */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-3 py-2 bg-gray-900/95 rounded-lg border border-gray-800 animate-in slide-in-from-top-2 duration-200">
            {/* Navigation Items */}
            <div className="border-b border-gray-800 pb-2 mb-2">
              {tabs.map((tab) => (
                <MobileMenuItem
                  key={tab.id}
                  label={tab.label}
                  isActive={activeTab === tab.id}
                  onClick={() => handleMobileNavigation(tab.path)}
                  icon={tab.icon}
                  color={tab.color}
                  badge={tab.badge}
                />
              ))}
            </div>

            {/* Credits Display */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-800 mb-2">
              <span className="text-sm text-gray-400">{t('nav.credits')}</span>
              <span className="text-lg font-bold text-white">
                {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining || 0}
              </span>
            </div>

            {/* Upgrade Button */}
            <div className="px-4 py-2">
              <Button 
                onClick={() => handleMobileNavigation(getLocalePath('pricing'))}
                className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-sm h-10"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {t('nav.upgrade')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
